import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AutoAwesomeRounded,
  HomeRounded,
  ChecklistRounded,
  ForumRounded,
  InsightsRounded,
  SchoolRounded,
  LogoutRounded,
  ExpandMoreRounded,
  FactCheckRounded,
} from '@mui/icons-material';
import { useTeacherAuth } from '../../context/TeacherAuthContext';
import {
  resolveTeacherClassContext,
  saveTeacherClassContext,
} from '../../utils/teacherClassContext';

export type TeacherNavKey = 'home' | 'plan' | 'homework' | 'chat' | 'report' | 'classes';

interface NavItem {
  key: TeacherNavKey;
  label: string;
  shortLabel: string;
  path: string;
  Icon: React.ComponentType<{ fontSize?: 'small' | 'inherit' | 'large' | 'medium' }>;
}

const NAV_ITEMS: NavItem[] = [
  // Chat first — the coach is the teacher's primary surface.
  { key: 'chat', label: 'Ask the curiosity', shortLabel: 'Curiosity', path: '/ask-coach', Icon: ForumRounded },
  { key: 'home', label: 'Dashboard', shortLabel: 'Dashboard', path: '/teacher-dashboard', Icon: HomeRounded },
  { key: 'plan', label: 'Curriculum', shortLabel: 'Curriculum', path: '/class-weekly-plan', Icon: ChecklistRounded },
  { key: 'homework', label: 'Homework', shortLabel: 'Homework', path: '/class-homework', Icon: FactCheckRounded },
  { key: 'report', label: 'Reports', shortLabel: 'Reports', path: '/class-report', Icon: InsightsRounded },
  { key: 'classes', label: 'Classes', shortLabel: 'Classes', path: '/class-details', Icon: SchoolRounded },
];

const initials = (name?: string | null) => {
  if (!name) return 'T';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'T';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const SUBJECT_LABELS: Record<string, string> = {
  maths: 'Maths',
  science: 'Science',
  social: 'Social',
};

interface TeacherLayoutProps {
  active: TeacherNavKey;
  children: React.ReactNode;
  // When true, the content area fills the viewport height and does NOT scroll —
  // the child owns its own internal scroll (used by the chat so the header and
  // composer stay put while only the messages scroll).
  fillViewport?: boolean;
}

const TeacherLayout: React.FC<TeacherLayoutProps> = ({ active, children, fillViewport = false }) => {
  const navigate = useNavigate();
  const { teacher, logoutTeacher } = useTeacherAuth();

  // The class context drives the nav-carried {school,grade,section} state so
  // switching screens keeps the selected class — exactly as TeacherDashboard does.
  const ctx = useMemo(() => resolveTeacherClassContext(), []);
  const grade =
    typeof ctx.grade === 'number' ? ctx.grade : ctx.grade ? Number(ctx.grade) : undefined;
  const section = ctx.section ?? null;
  const school = ctx.school ?? teacher?.school;

  const navState = {
    school,
    grade,
    section: section ?? undefined,
  };

  const classLabel =
    grade != null && !Number.isNaN(grade)
      ? `Grade ${grade}${section ? ` · ${section}` : ''}`
      : 'Pick a class';

  const subjectsLabel = useMemo(() => {
    const subs = teacher?.subjects ?? [];
    if (subs.length === 0) return school || '';
    return subs.map((s) => SUBJECT_LABELS[s] || s).join(', ');
  }, [teacher, school]);

  const go = (item: NavItem) => {
    if (grade != null && !Number.isNaN(grade) && school) {
      saveTeacherClassContext({ school, grade, section });
    }
    navigate(item.path, { state: navState });
  };

  const goClasses = () => {
    navigate('/teacher-view', { state: navState });
  };

  const handleLogout = () => {
    logoutTeacher();
    navigate('/teacher-login');
  };

  return (
    <div className="min-h-screen bg-[#F4F2FC] text-slate-900">
      <div className="mx-auto grid min-h-screen max-w-[1260px] grid-cols-1 md:grid-cols-[250px_minmax(0,1fr)]">
        {/* Desktop sidebar */}
        <aside className="sticky top-0 hidden h-screen flex-col gap-1 self-start border-r border-[#E9E6F4] px-4 py-5 md:flex">
          <div className="flex items-center gap-3 px-2 pb-3 pt-1">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 text-white">
              <AutoAwesomeRounded fontSize="small" />
            </span>
            <span>
              <span className="block text-base font-bold leading-tight">Curiosity</span>
              <span className="text-xs font-semibold text-slate-400">Teacher</span>
            </span>
          </div>

          <button
            type="button"
            onClick={goClasses}
            className="mb-1.5 flex w-full items-center gap-2.5 rounded-2xl border border-[#E9E6F4] bg-violet-50 px-3 py-2.5 text-left transition-colors hover:border-violet-200"
          >
            <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-violet-100 text-violet-700">
              <SchoolRounded fontSize="small" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-bold text-slate-900">{classLabel}</span>
              <span className="block truncate text-xs text-slate-500">{subjectsLabel}</span>
            </span>
            <ExpandMoreRounded fontSize="small" className="text-slate-400" />
          </button>

          <nav className="mt-1 flex flex-col gap-0.5" aria-label="Sections">
            {NAV_ITEMS.map((item) => {
              const isActive = item.key === active;
              const { Icon } = item;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => go(item)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-violet-100 text-violet-700'
                      : 'text-slate-500 hover:bg-[#F1EFFA] hover:text-slate-900'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon fontSize="small" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div className="mt-auto border-t border-[#E9E6F4] pt-2.5">
            <div className="flex items-center gap-3 px-2 pb-1 pt-1">
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                {initials(teacher?.name)}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold leading-tight">
                  {teacher?.name || teacher?.username || 'Teacher'}
                </span>
                <span className="block truncate text-xs text-slate-400">{teacher?.school}</span>
              </span>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="mt-1 flex w-full items-center gap-2.5 rounded-xl border border-[#E9E6F4] bg-white px-3 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:border-rose-300 hover:text-rose-600"
            >
              <LogoutRounded fontSize="small" /> Log out
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main
          className={`min-w-0 ${
            fillViewport
              ? 'flex h-[100dvh] flex-col overflow-hidden px-4 pt-5 md:px-8 md:pt-7'
              : 'px-4 pb-24 pt-5 md:px-8 md:pb-16 md:pt-7'
          }`}
        >
          {/* Mobile top bar */}
          <div className="mb-4 flex items-center justify-between md:hidden">
            <span className="flex items-center gap-2">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 text-white">
                <AutoAwesomeRounded fontSize="small" />
              </span>
              <b className="text-[0.95rem] font-bold">{classLabel}</b>
            </span>
            <span className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                {initials(teacher?.name)}
              </span>
              <button
                type="button"
                onClick={handleLogout}
                aria-label="Log out"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E9E6F4] bg-white text-slate-500 hover:text-rose-600"
              >
                <LogoutRounded fontSize="small" />
              </button>
            </span>
          </div>

          {children}
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-20 flex justify-around gap-px border-t border-[#E9E6F4] bg-white/95 px-1 py-2 backdrop-blur md:hidden"
        aria-label="Sections"
      >
        {NAV_ITEMS.map((item) => {
          const isActive = item.key === active;
          const { Icon } = item;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => go(item)}
              className={`flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 text-[0.64rem] font-semibold ${
                isActive ? 'text-violet-700' : 'text-slate-400'
              }`}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon fontSize="small" />
              {item.shortLabel}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default TeacherLayout;
